/**
 * RhymeGroupCluster - Dense pill flow renderer for rhyming word groups.
 *
 * Returns a React.Fragment of pills so the parent's flex-wrap container
 * can flow all pills together regardless of group boundaries. No box
 * containers, no category headers — just pills with colored left borders.
 *
 * V4 Outlined Style: white bg, full colored border, colored text.
 * Key Word System: first word in group (or user override) gets a ◆ indicator
 *   and a thicker left border. Non-key-words get per-pill category color.
 *
 * Click a pill → context menu appears. Click outside → it disappears.
 * Only one menu open at a time.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { FC } from 'react'
import type { Word } from '../../types/wordCollection'
import type { RhymeGroup } from '../../types/wordCollection'
import { RhymeCategory } from '../../utils/rhyme/types'

// ---------------------------------------------------------------------------
// Color mapping: V4 outlined — white bg, colored border + text
// ---------------------------------------------------------------------------

const CATEGORY_COLOR_MAP: Record<string, { borderColor: string; textColor: string; hoverBorderColor: string }> = {
  [RhymeCategory.PERFECT]:         { borderColor: '#22c55e', textColor: '#166534', hoverBorderColor: '#16a34a' },
  [RhymeCategory.FAMILY_VOICED]:   { borderColor: '#3b82f6', textColor: '#1e40af', hoverBorderColor: '#2563eb' },
  [RhymeCategory.FAMILY_UNVOICED]: { borderColor: '#3b82f6', textColor: '#1e40af', hoverBorderColor: '#2563eb' },
  [RhymeCategory.ADDITIVE]:        { borderColor: '#06b6d4', textColor: '#155e75', hoverBorderColor: '#0891b2' },
  [RhymeCategory.SUBTRACTIVE]:     { borderColor: '#eab308', textColor: '#854d0e', hoverBorderColor: '#ca8a04' },
  [RhymeCategory.ASSONANCE]:       { borderColor: '#f97316', textColor: '#9a3412', hoverBorderColor: '#ea580c' },
  [RhymeCategory.CONSONANCE]:      { borderColor: '#a855f7', textColor: '#6b21a8', hoverBorderColor: '#9333ea' },
  [RhymeCategory.NONE]:            { borderColor: '#d6d3d1', textColor: '#78716c', hoverBorderColor: '#a8a29e' },
}

const FALLBACK_COLOR = { borderColor: '#d6d3d1', textColor: '#78716c', hoverBorderColor: '#a8a29e' }

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface RhymeGroupClusterProps {
  group: RhymeGroup
  /** The actual Word objects for this group, mapped from group.words */
  words: Word[]
  usedWordsInLyrics: Set<string>
  /** Word ID of the key word for this group. Null for singletons (no key word). */
  keyWordId: string | null
  /** Per-word category relative to the key word. Key word maps to group dominantCategory. */
  wordCategories: Map<string, RhymeCategory>
  onSetAside: (word: Word) => void
  onExploreRhymes: (word: string, wordId?: string) => void
  onExploreThesaurus: (word: string, wordId?: string) => void
  onUpdateNotes: (wordId: string, notes: string) => void
  /** Called when user selects "Make Key Word" from context menu */
  onMakeKeyWord: (groupKey: string, wordId: string) => void
}

// ---------------------------------------------------------------------------
// Context menu — renders inside each pill, positioned absolutely below it
// ---------------------------------------------------------------------------

interface PillMenuProps {
  word: Word
  groupKey: string
  isKeyWord: boolean
  isSingleton: boolean
  onSetAside: (word: Word) => void
  onExploreRhymes: (word: string, wordId?: string) => void
  onExploreThesaurus: (word: string, wordId?: string) => void
  onUpdateNotes: (wordId: string, notes: string) => void
  onMakeKeyWord: (groupKey: string, wordId: string) => void
  onClose: () => void
}

const PillMenu: FC<PillMenuProps> = ({
  word,
  groupKey,
  isKeyWord,
  isSingleton,
  onSetAside,
  onExploreRhymes,
  onExploreThesaurus,
  onUpdateNotes,
  onMakeKeyWord,
  onClose,
}) => {
  const menuRef = useRef<HTMLDivElement>(null)
  const [focusedIndex, setFocusedIndex] = useState(0)

  // Build menu items based on pill role in the group.
  // Key word: no "Make Key Word" item, badge shows status instead.
  // Non-key-word in multi-group: includes "Make Key Word".
  // Singleton: plain 4-item menu, no key word concept.
  const menuItems = useMemo(() => {
    const base = [
      {
        id: 'explore-rhymes',
        icon: '🔍',
        label: 'Explore Rhymes',
        action: () => { onExploreRhymes(word.word, word.id); onClose() },
        danger: false,
      },
      {
        id: 'explore-thesaurus',
        icon: '📖',
        label: 'Thesaurus',
        action: () => { onExploreThesaurus(word.word, word.id); onClose() },
        danger: false,
      },
      {
        id: 'add-note',
        icon: '✏️',
        label: 'Add Note',
        action: () => { onUpdateNotes(word.id, ''); onClose() },
        danger: false,
      },
    ]

    const makeKeyWordItem = {
      id: 'make-key-word',
      icon: '◆',
      label: 'Make Key Word',
      action: () => { onMakeKeyWord(groupKey, word.id); onClose() },
      danger: false,
    }

    const setAsideItem = {
      id: 'set-aside',
      icon: '✖',
      label: 'Set Aside',
      action: () => { onSetAside(word); onClose() },
      danger: true,
    }

    // Singletons and key words skip the "Make Key Word" option
    if (isSingleton || isKeyWord) {
      return [...base, setAsideItem]
    }

    return [...base, makeKeyWordItem, setAsideItem]
  }, [word, groupKey, isKeyWord, isSingleton, onExploreRhymes, onExploreThesaurus, onUpdateNotes, onMakeKeyWord, onSetAside, onClose])

  // Keyboard navigation: ArrowDown/Up, Enter, Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'Escape':
          e.preventDefault()
          onClose()
          break
        case 'ArrowDown':
          e.preventDefault()
          setFocusedIndex(prev => (prev + 1) % menuItems.length)
          break
        case 'ArrowUp':
          e.preventDefault()
          setFocusedIndex(prev => (prev - 1 + menuItems.length) % menuItems.length)
          break
        case 'Enter':
          e.preventDefault()
          menuItems[focusedIndex].action()
          break
        default:
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [focusedIndex, menuItems, onClose])

  // Track menu items to determine which items need separators.
  // Separator rules:
  //   - Before "Make Key Word" (separates explore actions from role actions)
  //   - Before "Set Aside" (danger zone separator)
  const needsSeparatorBefore = (itemId: string): boolean => {
    if (itemId === 'make-key-word') return true
    if (itemId === 'set-aside') {
      // Separator before set-aside only when make-key-word is NOT present
      // (when it IS present, set-aside already has a separator from make-key-word)
      return !menuItems.some(i => i.id === 'make-key-word')
    }
    return false
  }

  return (
    <div
      ref={menuRef}
      className="absolute rounded-md border border-gray-200 bg-white shadow-lg"
      style={{
        top: 'calc(100% + 3px)',
        left: '-1px',
        zIndex: 100,
        minWidth: '152px',
      }}
      data-testid={`dense-pill-menu-${word.id}`}
      role="menu"
      // Stop propagation so clicks inside the menu don't bubble to the pill,
      // which would re-toggle the menu open immediately after it closed.
      onClick={e => e.stopPropagation()}
    >
      {/* Key word badge — non-interactive, shows the pill's role in the group */}
      {isKeyWord && (
        <>
          <div
            className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-amber-700"
            style={{ background: '#fffbeb' }}
            data-testid="dense-pill-menu-key-word-badge"
          >
            <span>◆</span>
            <span>Key Word</span>
          </div>
          <div className="my-1 h-px bg-gray-100" role="separator" />
        </>
      )}
      {menuItems.map((item, index) => {
        const showSeparator = needsSeparatorBefore(item.id)
        return (
          <Fragment key={item.id}>
            {showSeparator && (
              <div className="my-1 h-px bg-gray-100" role="separator" />
            )}
            <button
              type="button"
              role="menuitem"
              className={`flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs transition-colors ${
                item.danger
                  ? `text-gray-700 hover:bg-red-50 hover:text-red-700 ${focusedIndex === index ? 'bg-red-50 text-red-700' : ''}`
                  : `text-gray-700 hover:bg-gray-100 ${focusedIndex === index ? 'bg-gray-100' : ''}`
              }`}
              onClick={item.action}
              onMouseEnter={() => setFocusedIndex(index)}
              data-testid={`dense-pill-menu-item-${item.id}`}
            >
              <span>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          </Fragment>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main component — returns Fragment so pills flow in parent's flex-wrap
// ---------------------------------------------------------------------------

export const RhymeGroupCluster: FC<RhymeGroupClusterProps> = ({
  group,
  words,
  usedWordsInLyrics,
  keyWordId,
  wordCategories,
  onSetAside,
  onExploreRhymes,
  onExploreThesaurus,
  onUpdateNotes,
  onMakeKeyWord,
}) => {
  const [openMenuWordId, setOpenMenuWordId] = useState<string | null>(null)
  const [hoveredWordId, setHoveredWordId] = useState<string | null>(null)
  const isSingleton = words.length <= 1

  const toggleMenu = useCallback((wordId: string) => {
    setOpenMenuWordId(prev => (prev === wordId ? null : wordId))
  }, [])

  const closeMenu = useCallback(() => {
    setOpenMenuWordId(null)
  }, [])

  // Transparent fixed overlay to catch outside clicks when a menu is open.
  // Cheaper than document event listeners — avoids capture phase fights.
  const overlay = openMenuWordId !== null ? (
    <div
      className="fixed inset-0"
      style={{ zIndex: 99 }}
      onClick={closeMenu}
      aria-hidden
    />
  ) : null

  return (
    <>
      {overlay}
      {words.map(word => {
        const isUsed = usedWordsInLyrics.has(word.word) || usedWordsInLyrics.has(word.word.toLowerCase())
        const isMenuOpen = openMenuWordId === word.id
        const isKeyWord = !isSingleton && keyWordId === word.id
        const isHovered = hoveredWordId === word.id

        // Determine which category color to use for this specific pill:
        // - Key word: uses group's dominant category
        // - Non-key-word: uses per-pill category from wordCategories map
        // - Fallback: group's dominant category (covers singletons + unresolved)
        let pillCategory: RhymeCategory
        if (isKeyWord) {
          pillCategory = group.dominantCategory
        } else {
          pillCategory = wordCategories.get(word.id) ?? group.dominantCategory
        }

        const { borderColor, textColor, hoverBorderColor } =
          CATEGORY_COLOR_MAP[pillCategory] ?? FALLBACK_COLOR

        const activeBorderColor = isHovered ? hoverBorderColor : borderColor

        return (
          <div
            key={word.id}
            className="relative inline-flex items-center cursor-pointer select-none transition-all duration-100"
            style={{
              // Key word gets a thicker left border to distinguish it visually
              borderLeft: `${isKeyWord ? 4 : 3}px solid ${activeBorderColor}`,
              border: `1px solid ${activeBorderColor}`,
              borderLeftWidth: isKeyWord ? '4px' : '3px',
              padding: '3px 7px 3px 8px',
              borderRadius: '3px',
              fontSize: '11.5px',
              fontFamily: "'JetBrains Mono', ui-monospace, monospace",
              fontWeight: isKeyWord ? 600 : 500,
              background: isKeyWord ? '#f8f8f7' : '#ffffff',
              color: textColor,
              // Keep the menu above the overlay
              zIndex: isMenuOpen ? 100 : 'auto',
            }}
            onClick={() => toggleMenu(word.id)}
            onMouseEnter={() => setHoveredWordId(word.id)}
            onMouseLeave={() => setHoveredWordId(null)}
            data-testid={`dense-pill-${word.id}`}
          >
            {/* Key word diamond indicator */}
            {isKeyWord && (
              <span
                style={{ marginRight: '4px', fontSize: '9px', opacity: 0.8 }}
                aria-label="key word"
                data-testid={`dense-pill-key-indicator-${word.id}`}
              >
                ◆
              </span>
            )}
            {word.word}
            {isUsed && (
              <span
                className="absolute top-0.5 right-0.5 h-1 w-1 rounded-full bg-green-500"
                data-testid={`dense-pill-used-${word.id}`}
                aria-label="used in lyrics"
              />
            )}
            {isMenuOpen && (
              <PillMenu
                word={word}
                groupKey={group.phoneticKey}
                isKeyWord={isKeyWord}
                isSingleton={isSingleton}
                onSetAside={onSetAside}
                onExploreRhymes={onExploreRhymes}
                onExploreThesaurus={onExploreThesaurus}
                onUpdateNotes={onUpdateNotes}
                onMakeKeyWord={onMakeKeyWord}
                onClose={closeMenu}
              />
            )}
          </div>
        )
      })}
    </>
  )
}

export default RhymeGroupCluster
